import { useApiCatalogTreeV3GetIntegrations } from '@repo/api-codegen/v2';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { EntityType } from '@repo/common/enums/entityType';
import { useNavigateHandler } from '@repo/common/hooks/useNavigate';
import type React from 'react';
import { useCallback } from 'react';
import { useEntityBreadcrumbTeam } from '../../../hooks/useEntityBreadcrumbs';
import { buildResourceUrl } from '../../../utils/navigationUtils';
import { DataTreeLoader } from '../components/DataTreeLoader';
import { EntityTreeNode } from '../components/EntityTreeNode';
import type { CommonEntityTreeNode } from '../utils';
import {
	catalogFilterFromTreeNode,
	catalogFilterQueryParamsFromCatalogTreeNode,
	getIcon,
	getLabel,
} from './utils';

interface CatalogTreeProps {
	teamId: string;
}

export function CatalogTreeV3({ teamId }: CatalogTreeProps) {
	const { data: integrations, isLoading: isLoadingIntegrations } =
		useApiCatalogTreeV3GetIntegrations(
			{
				queryParams: {
					team_id: teamId,
				},
			},
			{
				select: (data) =>
					data
						.filter((integration) => {
							const integrationSpec = integrationList.find(
								(spec) => spec.type === integration.type
							);
							return !integrationSpec?.isExtension;
						})
						.map((integration) => ({
							id: integration.id,
							hasChildren: true,
							type: 'integration',
							teamId,
							integration,
						})) || [],
			}
		);

	const { setTeamBreadcrumb } = useEntityBreadcrumbTeam();

	const navigateHandler = useNavigateHandler();

	const handleNavigate = useCallback(
		(event: React.MouseEvent, node: CommonEntityTreeNode) => {
			setTeamBreadcrumb(teamId);
			const catalogFilterQueryParams =
				catalogFilterQueryParamsFromCatalogTreeNode(node);

			const catalogPageURL = `/teams/${teamId}/catalog?filters=${encodeURIComponent(
				JSON.stringify(catalogFilterQueryParams)
			)}`;

			if (node.entity.entity_type !== EntityType.integration) {
				const resourceURL = buildResourceUrl(node.entity);
				if (resourceURL !== '#') {
					navigateHandler(event)(resourceURL);
					return;
				}
			}

			navigateHandler(event)(catalogPageURL);
		},
		[setTeamBreadcrumb, teamId, navigateHandler]
	);

	const getFilterOperands = useCallback(
		(node: CommonEntityTreeNode) => catalogFilterFromTreeNode(node, teamId),
		[teamId]
	);

	if (isLoadingIntegrations) {
		return <DataTreeLoader />;
	}

	return (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>
			{integrations?.map((node) => (
				<EntityTreeNode
					key={node.id}
					node={{
						id: node.id,
						hasChildren: node.hasChildren,
						integration: node.integration,
						entity: {
							id: node.id,
							entity_type: EntityType.integration,
							icon: '',
							title: node.integration?.name ?? '',
						},
					}}
					getIcon={getIcon}
					level={0}
					onClick={(e, commonNode) => handleNavigate(e, commonNode)}
					getLabel={getLabel}
					childFilterOperands={getFilterOperands}
				/>
			))}
		</>
	);
}
